import { useEffect, useState } from "react";
import { defaultFilter } from "cmdk";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../../components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../../../components/ui/command";
import {
  SelectorPlaceholder,
  SelectorTrigger,
  SelectorTriggerLabel,
} from "../../../../components/ui/selectors/selector-trigger";
import { useMenuNavMode } from "../../../../components/ui/selectors/use-menu-nav-mode";
import { useTriggerWidth } from "../../../../components/ui/use-trigger-width";
import type { MediaCollection } from "../../../../api/media-library";
import { CollectionColor } from "../collection-color";
import { cn } from "../../../../lib/utils";

export const collectionMenuClassName =
  "group/menu h-auto max-h-[min(60vh,var(--radix-popover-content-available-height,60vh))] overflow-hidden rounded-lg border-neutral-100 pb-1 font-normal shadow-[0_4px_8px_-2px_rgba(16,24,40,0.10),0_2px_4px_-2px_rgba(16,24,40,0.06)]";

export const collectionMenuRowClassName = [
  "flex h-10 cursor-pointer items-center gap-2 rounded-none px-3.5 py-2 font-inter text-sm font-medium text-neutral-800",
  "data-[chosen]:bg-blue-50 data-[chosen]:hover:bg-blue-50 data-[chosen]:data-[selected=true]:bg-blue-50",
  "hover:bg-neutral-50 data-[selected=true]:bg-neutral-50 group-data-[nav=keyboard]/menu:data-[selected=true]:ring-1 group-data-[nav=keyboard]/menu:data-[selected=true]:ring-inset group-data-[nav=keyboard]/menu:data-[selected=true]:ring-blue-500",
].join(" ");

// Search the name (the row's keywords) only: the hex id in each row's value
// would fuzzy-match almost any short query.
export function filterCollectionsByName(
  _value: string,
  search: string,
  keywords?: string[]
) {
  return defaultFilter(keywords?.join(" ") ?? "", search);
}

export function CollectionMenuTick() {
  return (
    <>
      <i
        aria-hidden
        className="fa-regular fa-check shrink-0 text-[16px] leading-4 text-blue-500"
      />
      <span className="sr-only">Selected</span>
    </>
  );
}

interface Props {
  collections: Array<MediaCollection>;
  selectedCollection: MediaCollection | null;
  className?: string;
  disabled?: boolean;
  triggerMaxWidth?: number;
  onChange(value: string | string[]): void;
}

export function CollectionSelector(props: Props) {
  const [open, setOpen] = useState(false);
  const { navMode, navHandlers } = useMenuNavMode(open);
  const { triggerRef, triggerWidthStyle } = useTriggerWidth<HTMLButtonElement>(
    open,
    240
  );
  const [currentSelectedCollection, setCurrentSelectedCollection] = useState(
    props.selectedCollection?._id || null
  );

  useEffect(() => {
    if (props.selectedCollection?._id !== currentSelectedCollection) {
      setCurrentSelectedCollection(props.selectedCollection?._id ?? null);
    }
  }, [props.selectedCollection?._id]);

  const handleChange = (collectionId: string) => {
    setCurrentSelectedCollection(collectionId);
    props.onChange(collectionId);
    setOpen(false);
  };

  const selectedCollection = props.collections.find(
    (c) => c._id === currentSelectedCollection
  );

  return (
    // Callers size this box; without a cap the picker keeps its 240px default.
    <div
      className={cn(
        props.triggerMaxWidth === undefined && "w-full md:w-[240px]",
        props.className
      )}
    >
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <SelectorTrigger
            ref={triggerRef}
            size="sm"
            open={open}
            disabled={props.disabled}
            maxWidth={props.triggerMaxWidth}
            aria-label="Collection"
          >
            {selectedCollection ? (
              <>
                <CollectionColor
                  collection={selectedCollection}
                  className="mr-2 shrink-0"
                />
                <SelectorTriggerLabel className="text-neutral-800">
                  {selectedCollection.name}
                </SelectorTriggerLabel>
              </>
            ) : (
              <SelectorPlaceholder>Choose collection...</SelectorPlaceholder>
            )}
          </SelectorTrigger>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={4}
          collisionPadding={8}
          motion="rise"
          widthMode="trigger"
          style={triggerWidthStyle}
          className="rounded-lg border-0 p-0 shadow-none"
        >
          <Command
            filter={filterCollectionsByName}
            defaultValue={
              selectedCollection
                ? `${selectedCollection.name} ${selectedCollection._id}`.trim()
                : undefined
            }
            label="Collections"
            onKeyDown={navHandlers.onKeyDown}
            onPointerMove={navHandlers.onPointerMove}
            data-nav={navMode}
            className={collectionMenuClassName}
          >
            <CommandInput
              placeholder="Search collections..."
              wrapperClassName="h-10 shrink-0 px-3.5"
            />
            <CommandList className="min-h-0 max-h-none flex-1">
              <CommandEmpty>No collections found.</CommandEmpty>
              <CommandGroup className="overflow-visible p-0 pt-1">
                {props.collections.map((collection) => {
                  const isSelected =
                    currentSelectedCollection === collection._id;
                  return (
                    <CommandItem
                      key={collection._id}
                      value={`${collection.name} ${collection._id}`}
                      keywords={[collection.name]}
                      onSelect={() => handleChange(collection._id)}
                      data-chosen={isSelected || undefined}
                      className={collectionMenuRowClassName}
                    >
                      <CollectionColor
                        collection={collection}
                        className="shrink-0"
                      />
                      <span className="min-w-0 flex-1 truncate">
                        {collection.name}
                      </span>
                      {isSelected ? <CollectionMenuTick /> : null}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
